<template>
  <div class="container-menus">
    <div class="titulo-desc">
      <h1>Perfil de Usuário</h1>
      <h2>
        Gerencie as informações do seu perfil pessoal, que é exibido para outros
        usuários.
      </h2>
    </div>
    <div class="fomulario-perfil-usuario">
      <form @submit.prevent="handleSubmit" class="form-container" novalidate>
        <div class="form-section">
          <div class="input-center">
            <label for="fullName" class="sr-only"
              >Nome completo<span class="required">*</span></label
            >
            <input
              id="fullName"
              type="text"
              placeholder="Nome completo"
              class="form-input"
              v-model="formData.fullName"
              :class="{ 'input-error': errors.fullName }"
              required
            />
            <p v-if="errors.fullName" class="error-message">
              {{ errors.fullName }}
            </p>
          </div>
          <div class="input-center">
            <label for="fullnameuser" class="sr-only">
              Nome Público<span class="required">*</span>
              <img
                src="../../assets/Tooltip.svg"
                alt="Informação"
                title="O nome público é o nome que será utilizado no portal. Ex: José Silva "
                style="
                  width: 16px;
                  height: 16px;
                  margin-left: 4px;
                  cursor: pointer;
                "
              />
            </label>
            <input
              id="fullnameuser"
              type="text"
              placeholder="Nome a ser exibido no seu perfil de usuário"
              class="form-input"
              v-model="formData.publicUser"
              :class="{ 'input-error': errors.publicUser }"
              required
            />
            <p v-if="errors.publicUser" class="error-message">
              {{ errors.publicUser }}
            </p>
          </div>
        </div>

        <div class="divider"></div>

        <div class="form-section">
          <div class="input-center" id="input-date">
            <label for="birthDate" class="sr-only">Data de nascimento</label>
            <input
              id="birthDate"
              type="text"
              placeholder="Formato padrão: DD/MM/AAAA"
              class="form-input"
              v-model="formattedBirthDate"
              :class="{ 'input-error': errors.birthDate }"
            />
            <p v-if="errors.birthDate" class="error-message">
              {{ errors.birthDate }}
            </p>
          </div>
          <div class="input-center" id="input-link">
            <label for="link" class="sr-only">Inserir Link</label>
            <input
              id="link"
              v-model="formData.link"
              type="url"
              class="form-input"
              placeholder="Insira o link para acessar o seu currículo Lattes"
            />
            <p v-if="errors.link" class="error-message">{{ errors.link }}</p>
          </div>
        </div>

        <div class="divider"></div>

        <div class="form-section">
          <div class="input-center">
            <label for="educationLevel" class="sr-only"
              >Nível de escolaridade<span class="required">*</span></label
            >
            <select
              v-if="education.length > 0"
              id="educationLevel"
              class="form-input"
              v-model="formData.educationLevel"
              :class="{ 'input-error': errors.educationLevel }"
              :disabled="isLoadingEducationLevels"
              required
            >
              <option value="">Selecione um nível</option>
              <option v-for="item in education" :key="item.id" :value="item.id">
                {{ item.name }}
              </option>
            </select>

            <p v-if="errors.educationLevel" class="error-message">
              {{ errors.educationLevel }}
            </p>
          </div>
          <div class="input-center">
            <label for="institution" class="sr-only">Instituição</label>
            <input
              id="institution"
              type="text"
              placeholder="Nome da instituição que você faz parte"
              class="form-input"
              v-model="formData.institution"
              :class="{ 'input-error': errors.institution }"
            />
            <p v-if="errors.institution" class="error-message">
              {{ errors.institution }}
            </p>
          </div>
        </div>

        <div class="divider"></div>

        <div>
          <label for="bio" class="sr-only">Bio</label>
          <textarea
            id="bio"
            placeholder="Fale um pouco sobre você, seus interesses, áreas de interesse, etc..."
            class="form-textarea"
            v-model="formData.bio"
          ></textarea>
        </div>

        <div class="divider"></div>
        <label for="profilePhoto" class="sr-only">Foto do perfil</label>

        <div class="form-section" style="gap: 32px">
          <div class="file-upload">
            <img
              id="avatar"
              :src="
                formData.avatar && formData.avatar !== 'REMOVE_AVATAR'
                  ? formData.avatar
                  : '../image/Avatar.svg'
              "
              alt="Foto de perfil"
            />
            <div id="container-remove-foto">
              <p class="form-link" @click="removePhoto">
                <strong>Excluir foto atual</strong>
              </p>
            </div>
          </div>
          <div id="input-image" @click="triggerFileInput">
            <img src="../../assets/upload.svg" />
            <h1 id="image-upload-h1">
              Clique aqui para fazer upload da sua foto de perfil.
            </h1>
            <input
              type="file"
              id="profilePhoto"
              accept=".jpeg,.jpg,.png,.gif,image/jpeg,image/png,image/gif"
              @change="handleFileUpload"
              style="display: none"
            />
            <p class="form-hint">
              Os formatos de arquivo de imagem aceitos para o upload da sua foto
              de perfil incluem JPEG, PNG e GIF. Certifique-se de que a imagem
              não exceda 5 MB.
            </p>
          </div>
        </div>

        <div class="divider"></div>

        <div class="form-actions">
          <button type="submit" class="save-button">Salvar Alterações</button>
          <button type="button" class="cancel-button" @click="handleCancel">
            Cancelar
          </button>
          <span
            v-if="formHasErrors"
            class="error-message"
            style="font-size: 16px"
            >Por favor, preencha todos os campos obrigatórios corretamente.
            <span style="color: var(--gray)"
              >(<span class="required">* </span>)</span
            ></span
          >
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import api from "@/services/api";
import { ref, reactive, computed, watch, onMounted } from "vue";
import { validateLinkLattes, validarDataNascimento } from "@/utils/validations";
import { useStore } from "vuex";

const education = ref([]);
const isLoadingEducationLevels = ref(false);
const fetchEducationLevels = async () => {
  try {
    isLoadingEducationLevels.value = true;
    const response = await api.get("education");
    education.value = response.data; // Armazena o array completo de objetos
  } catch (error) {
    console.error("Erro ao buscar níveis de escolaridade:", error);
  } finally {
    isLoadingEducationLevels.value = false;
  }
};

// ========== REACTIVE STATE ==========
const store = useStore();
const user = computed(() => store.getters.getUser);
const formData = reactive({
  avatar: "",
  publicUser: "",
  fullName: "",
  birthDate: "",
  educationLevel: "",
  institution: "",
  link: "",
  bio: "",
  profilePhoto: null,
});

const errors = reactive({
  fullName: "",
  publicUser: "",
  birthDate: "",
  link: "",
  educationLevel: "",
  institution: "",
});

const formHasErrors = ref(false);

// ========== UTILITY FUNCTIONS ==========
function imageToBase64(file, callback) {
  const reader = new FileReader();

  // Quando o arquivo for lido com sucesso, chamamos o callback passando a string base64 completa
  reader.onload = (event) => {
    // A string base64 contém o prefixo 'data:image/png;base64,' ou similar, e será passada completa
    const base64String = event.target.result; // A string base64 completa com o prefixo
    callback(base64String); // Passa a string base64 para o callback
  };

  // Se ocorrer erro ao ler o arquivo, chama o callback com erro
  reader.onerror = (error) => {
    callback(null, error);
  };

  // Lê o arquivo como base64
  reader.readAsDataURL(file);
}

const formatDate = (dateString) => {
  const [day, month, year] = dateString.split("/"); // dd/mm/yyyy
  return `${year}-${month}-${day}`; // yyyy-mm-dd
};

const triggerFileInput = () => {
  document.getElementById("profilePhoto").click();
};

const getAuthHeader = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    "Content-Type": "multipart/form-data",
  },
});

// ========== USER DATA MANAGEMENT ==========
const updateFormData = (userData) => {
  const educationName = userData.education || "";

  // Encontra o objeto de educação correspondente ao NOME do usuário
  const userEducation = education.value.find(
    (item) => item.name === educationName
  );

  // Atualiza o formData garantindo que educationLevel seja o ID correto
  formData.avatar = userData.avatar || "";
  formData.fullName = userData.name || "";
  formData.publicUser = userData.public_name || "";
  formData.birthDate = userData.birthdate || "";
  formData.educationLevel = userEducation ? userEducation.id : "";
  formData.institution = userData.institution || "";
  formData.link = userData.lattes_url || "";
  formData.bio = userData.biography || "";
  formData.profilePhoto = null;
};

const fetchUserData = async () => {
  try {
    let accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");

    try {
      const response = await api.get(`/users/${user.value.id}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      updateFormData(response.data);
    } catch (error) {
      if (error.response?.status === 401 && refreshToken) {
        const newTokens = await refreshTokens(refreshToken);
        const response = await api.get(`/users/${user.value.id}/`, {
          headers: { Authorization: `Bearer ${newTokens.accessToken}` },
        });
        updateFormData(response.data);
      } else {
        console.error("Erro ao carregar dados do usuário:", error);
      }
    }
  } catch (error) {
    console.error("Erro geral:", error);
  }
};

const refreshTokens = async (refreshToken) => {
  const response = await api.post("/login/refresh/", { refresh: refreshToken });
  localStorage.setItem("access_token", response.data.access);
  if (response.data.refresh) {
    localStorage.setItem("refresh_token", response.data.refresh);
  }
  return {
    accessToken: response.data.access,
    refreshToken: response.data.refresh || refreshToken,
  };
};

// ========== FORM VALIDATION ==========
const validateField = (field, value) => {
  errors[field] = "";

  switch (field) {
    case "birthDate":
      if (value && value.trim() !== "") {
        // Só valida se não for vazio
        const dateParts = value.split("-");
        const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
        if (!validarDataNascimento(formattedDate)) {
          errors.birthDate = "Data inválida!";
        }
      }
      break;

    case "link":
      if (value) {
        const linkValid = validateLinkLattes(value);
        errors.link = typeof linkValid === "string" ? linkValid : "";
      }
      break;

    case "educationLevel":
      if (!value) {
        errors.educationLevel = "Nível de escolaridade é obrigatório!";
      } else if (!education.value.some((item) => item.id === value)) {
        errors.educationLevel = "Selecione um nível válido!";
      }
      break;

    case "publicUser":
      if (value && value.length > 20) {
        errors.publicUser = "Nome público não pode ter mais de 20 caracteres!";
      }
      break;

    case "institution":
      if (value && value.length > 250) {
        errors.institution = "Instituição não pode ter mais de 250 caracteres!";
      }
  }

  if (!value && ["publicUser", "fullName", "educationLevel"].includes(field)) {
    errors[field] = "Esse campo é obrigatório!";
  }
};

const setupFieldWatchers = () => {
  for (const field in formData) {
    watch(
      () => formData[field],
      (newValue) => {
        validateField(field, newValue);
      }
    );
  }
};

setupFieldWatchers();

// ========== COMPUTED FOR FORMATTING ==========
const formattedBirthDate = computed({
  get() {
    if (!formData.birthDate) return "";
    const [year, month, day] = formData.birthDate.split("-");
    return `${day}/${month}/${year}`;
  },
  set(value) {
    if (!value || value.trim() === "") {
      formData.birthDate = ""; // String vazia em vez de null
    } else {
      const [day, month, year] = value.split("/");
      formData.birthDate = `${year}-${month}-${day}`;
    }
  },
});

// ========== FORM ACTIONS ==========
const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file && file.size <= 5 * 1024 * 1024) {
    const reader = new FileReader();
    reader.onload = (e) => {
      formData.avatar = e.target.result;
      formData.profilePhoto = file;
    };
    reader.readAsDataURL(file);
  } else {
    alert("Arquivo muito grande! Tamanho máximo: 5MB.");
  }
  if (file) {
    imageToBase64(file, (base64String, error) => {
      if (error) {
        console.error("Erro ao converter imagem em base64", error);
      } else {
        formData.profilePhoto = base64String;
      }
    });
  }
};

const removePhoto = () => {
  // Usamos um valor especial para indicar remoção
  formData.avatar = "REMOVE_AVATAR";
  formData.profilePhoto = null;

  // Reseta o input file
  const fileInput = document.getElementById("profilePhoto");
  if (fileInput) {
    fileInput.value = "";
  }
};
const handleSubmit = async () => {
  formHasErrors.value = false;

  for (const field in errors) {
    validateField(field, formData[field]);
  }

  const hasError = Object.values(errors).some((err) => err);
  if (hasError) {
    formHasErrors.value = true;
    return;
  }

  try {
    const form = new FormData();
    const selectedEducation = education.value.find(
      (item) => item.id === formData.educationLevel
    );
    form.append("name", formData.fullName);
    form.append("public_name", formData.publicUser);
    form.append("birthdate", formData.birthDate || "");
    form.append("education", selectedEducation?.name || "");
    form.append("institution", formData.institution || "");
    form.append("lattes_url", formData.link || "");
    form.append("biography", formData.bio || "");

    if (formData.avatar === "REMOVE_AVATAR") {
      form.append("avatar", ""); // Envia null para remover o avatar
    } else if (formData.profilePhoto) {
      form.append("avatar", formData.profilePhoto); // Envia a nova foto
    }

    await api.patch(`/users/${user.value.id}/`, form, getAuthHeader());
    await fetchUserData();
    await store.dispatch("setBaoBahUser");
    window.location.reload();
  } catch (error) {
    console.error("Erro ao atualizar perfil:", error);
    alert("Erro ao salvar alterações. Tente novamente.");
  }
};

const handleCancel = () => {
  fetchUserData();
};

onMounted(async () => {
  try {
    // 1. Primeiro carrega os níveis de educação
    await fetchEducationLevels();

    // 2. Depois carrega os dados do usuário
    await fetchUserData();
  } catch (error) {
    console.error("Erro ao carregar dados:", error);
  }
});
</script>

<style scoped>
#avatar {
  width: 80px;
  height: 80px;
  gap: 10px;
  border-radius: 999px;
}
.input-error {
  border-color: red;
}

.error-message {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}
.form-label {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: #333;
}

.required {
  color: red;
  margin-left: 4px;
  font-size: 16px;
}
.form-textarea::placeholder,
.form-input::placeholder {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
}
.form-input {
  overflow-x: auto;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.form-input::-webkit-scrollbar {
  display: none;
}
#input-date {
  max-width: 264px;
}
#input-link {
  max-width: 100%;
  width: 100%;
}
.input-center {
  max-width: 440px;
  width: 100%;
}
.form-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

h1 {
  font-size: 24px;
  margin-bottom: 16px;
}

h2 {
  font-size: 20px;
  margin: 24px 0 16px 0;
}

.form-section {
  display: flex;
  margin-bottom: 16px;
  max-width: 896px;
  justify-content: space-between;
  gap: 16px;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 50px;
  border-width: 1px;
  padding: 8px 50px 8px 12px;
}

.form-textarea {
  height: 100px;
  resize: none;
  border-radius: 20px;
}

.form-hint {
  color: #655f5f;
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  margin: 0 32px;
  line-height: 16px;
  text-align: center;
}
#container-remove-foto {
  display: flex;
  text-align: center;
}
.form-link {
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  color: var(--terracota);
  cursor: pointer;
}

.divider {
  height: 1px;
  background-color: #eee;
  margin: 24px 0;
}
#image-upload-h1 {
  font-family: Inter;
  font-weight: 550;
  font-size: 16px;
  line-height: 28px;
  text-align: center;
  color: var(--terracota);
  margin: 0;
}
#input-image {
  display: flex;
  flex-direction: column;
  max-width: 734px;
  align-items: center;
  text-align: center;
  height: 196px;
  gap: 8px;
  border-radius: 8px;
  padding: 32px;
  border: 1px solid #cfcece;
}

.file-upload {
  padding: 40px 25px 40px 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.form-actions {
  display: flex;
  justify-content: flex-start;
  gap: 16px;
  margin-top: 24px;
}

.save-button {
  height: 33px;
  width: 140px;
  padding: 8px 16px;
  background-color: var(--terracota);
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-family: Lora;
  font-weight: 700;
  font-size: 12px;
  line-height: 120%;
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.cancel-button {
  padding: 8px 16px;
  background-color: transparent;
  border: 1px solid #ccc;
  border-radius: 50px;
  cursor: pointer;
  width: 84px;
  height: 33px;
  font-family: Lora;
  font-weight: 700;
  font-size: 14px;
  line-height: 120%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.container-menus {
  display: flex;
  padding: 16px;
  margin: 0 56px;
}

.titulo-desc h1 {
  font-family: Lora, serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.2;
}

.titulo-desc h2 {
  font-family: "DM Sans", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.2;
  color: #7a7474;
  max-width: 360px;
}
</style>
